import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Pencil, Play, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { VedaPathWatermark } from "@/components/brand/VedaPathWatermark";
import { useI18n } from "@/i18n/I18nContext";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import {
  fetchTownhallChant,
  fetchSlokas,
  subscribeTownhallInspiration,
  resolveTownhallImageSrc,
  postTownhallPresence,
} from "@/lib/townhall/townhall-service";
import { resolveChantDeityImage } from "@/lib/chanting/deity-images";
import {
  joinChanting,
  postChantProgress,
  getChantSessionId,
  setChantSessionId,
} from "@/lib/chanting/chanting-service";
import { InspirationDashboard } from "@/components/townhall/InspirationDashboard";
import { ScrollingSlokaPanel } from "@/components/townhall/ScrollingSlokaPanel";
import { AddMyCountForm } from "@/components/townhall/AddMyCountForm";

function DeityImage({ src, title, chantId, deity }) {
  const candidates = [
    resolveTownhallImageSrc(src),
    chantId ? resolveTownhallImageSrc(`/townhall/asset/${chantId}`) : null,
    resolveTownhallImageSrc(resolveChantDeityImage({ chantId, deity, preferR2: true })),
    resolveTownhallImageSrc(resolveChantDeityImage({ chantId, deity, preferR2: false })),
  ].filter((v, i, arr) => v && arr.indexOf(v) === i);
  const [idx, setIdx] = useState(0);
  const resolved = candidates[idx];
  const show = resolved && idx < candidates.length;

  if (!show) {
    return (
      <div
        className="w-28 h-28 rounded-full bg-gradient-to-br from-amber-100 to-primary/10 flex items-center justify-center mx-auto"
        data-testid="townhall-deity-image-fallback"
      >
        <span className="text-4xl font-sanskrit text-primary/80">ॐ</span>
      </div>
    );
  }
  return (
    <div className="relative inline-block mx-auto">
      <img
        key={resolved}
        src={resolved}
        alt={title || ""}
        className="w-28 h-28 rounded-full object-contain p-2 shadow-md border-2 border-amber-200/60 bg-white"
        onError={() => setIdx((i) => i + 1)}
        data-testid="townhall-deity-image"
      />
      <VedaPathWatermark className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap" />
    </div>
  );
}

/**
 * Townhall chant view — synced audio + scrolling slokas, offline count, inspiration dashboard.
 */
export function TownhallChantView({ token }) {
  const { chantId } = useParams();
  const { lang } = useI18n();
  const { isAdmin, canEditLibrary, isStaffRole } = useSacredAdmin();
  const canEditTownhall = isAdmin || canEditLibrary || isStaffRole;

  const [detail, setDetail] = useState(null);
  const [inspiration, setInspiration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [textOnly, setTextOnly] = useState(false);
  const [session, setSession] = useState(null);
  const [audioSeconds, setAudioSeconds] = useState(0);
  const [error, setError] = useState(null);

  const audioRef = useRef(null);
  const lastCycleRef = useRef(null);

  const refreshDetail = useCallback(async () => {
    const data = await fetchTownhallChant(chantId, { lang });
    // Prefer dedicated D1 /slokas endpoint so Prayer Hall + Townhall stay in sync.
    const d1Slokas = await fetchSlokas(chantId, { lang }).catch(() => []);
    if (d1Slokas?.length) {
      data.slides = d1Slokas;
    }
    setDetail(data);
    if (data.personal) setSession(data.personal);
    return data;
  }, [chantId, lang]);

  useEffect(() => {
    setLoading(true);
    refreshDetail()
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [refreshDetail]);

  useEffect(() => {
    const unsub = subscribeTownhallInspiration(setInspiration);
    return unsub;
  }, []);

  const beginSyncedChanting = async () => {
    setError(null);
    try {
      // Text-only mode when audio missing — still scroll slokas on a timer.
      if (!detail?.audio_file) {
        setSyncing(true);
        setTextOnly(true);
        setAudioSeconds(0);
        return;
      }

      const sid = getChantSessionId();
      const join = await joinChanting({
        chantId,
        targetRepetitions: null,
        privateMode: false,
        sessionId: sid,
        token,
      });
      setChantSessionId(join.session.session_id);
      setSession(join.session);
      lastCycleRef.current = join.cycle?.cycle_id;
      setSyncing(true);
      setTextOnly(false);
      postTownhallPresence({ chantId, sessionId: join.session.session_id });

      const el = audioRef.current;
      if (el && detail?.sync?.cycle && !detail.sync.cycle.in_gap) {
        el.currentTime = Math.min(detail.sync.cycle.offset_seconds, detail.duration_seconds - 0.5);
        el.play().catch(() => {
          setTextOnly(true);
        });
      }
    } catch (e) {
      // Chanting join may fail if Worker not redeployed — fall back to text scroll.
      setSyncing(true);
      setTextOnly(true);
      setError(null);
    }
  };

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !syncing || textOnly) return;

    const onTime = () => setAudioSeconds(el.currentTime);

    const onEnded = async () => {
      if (!session) return;
      try {
        const res = await postChantProgress({
          sessionId: session.session_id,
          chantId,
          cycleId: lastCycleRef.current,
          completedCycle: true,
          timeSpentSeconds: detail?.duration_seconds,
          token,
        });
        setSession(res.session);
        if (res.global_stats && detail) {
          setDetail((d) => (d ? { ...d, global_stats: res.global_stats } : d));
        }
        const fresh = await refreshDetail();
        if (fresh?.sync?.cycle && !fresh.sync.cycle.in_gap) {
          el.currentTime = fresh.sync.cycle.offset_seconds;
          el.play().catch(() => setTextOnly(true));
          lastCycleRef.current = fresh.sync.cycle.cycle_id;
        }
      } catch (e) {
        console.error(e);
      }
    };

    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnded);
    };
  }, [syncing, textOnly, session, chantId, detail, token, refreshDetail]);

  // Text-only scroll: advance through sloka timestamps without audio.
  useEffect(() => {
    if (!syncing || !textOnly || !detail?.slides?.length) return;
    const slides = detail.slides;
    const maxTs = Math.max(...slides.map((s) => Number(s.timestamp) || 0), 30);
    const started = Date.now();
    const id = setInterval(() => {
      const elapsed = ((Date.now() - started) / 1000) % (maxTs + 8);
      setAudioSeconds(elapsed);
    }, 400);
    return () => clearInterval(id);
  }, [syncing, textOnly, detail?.slides]);

  useEffect(() => {
    if (!syncing || !session?.session_id) return;
    postTownhallPresence({ chantId, sessionId: session.session_id });
    const id = setInterval(() => {
      postTownhallPresence({ chantId, sessionId: session.session_id });
    }, 45_000);
    return () => clearInterval(id);
  }, [syncing, session?.session_id, chantId]);

  const handleOfflineUpdated = (result) => {
    if (result.session) setSession(result.session);
    if (result.global_stats && detail) {
      setDetail((d) => (d ? { ...d, global_stats: result.global_stats } : d));
    }
    if (result.inspiration) setInspiration(result.inspiration);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen py-24 text-center">
        <p className="text-muted-foreground">Chant not found.</p>
        <Button asChild variant="link" className="mt-4">
          <Link to="/townhall">Back to Townhall</Link>
        </Button>
      </div>
    );
  }

  const personalCount = session?.completed_repetitions ?? detail.personal?.completed_repetitions ?? 0;
  const audioSrc = detail.audio_file?.startsWith("http")
    ? detail.audio_file
    : `/audio/chanting/${detail.audio_file}`;

  return (
    <div
      className="min-h-screen py-20 px-4 noise bg-gradient-to-b from-background to-amber-50/30"
      data-testid="townhall-chant-view"
      data-chant-id={chantId}
    >
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/townhall">
              <ArrowLeft className="w-4 h-4 mr-1" /> Townhall
            </Link>
          </Button>
          {canEditTownhall && (
            <Button size="sm" variant="outline" asChild data-testid="townhall-edit-btn">
              <Link to={`/townhall/${chantId}/edit`}>
                <Pencil className="w-4 h-4 mr-1" /> Edit
              </Link>
            </Button>
          )}
        </div>

        <div className="text-center mb-6">
          <Badge variant="outline" className="mb-2 border-amber-300 text-amber-800">
            VedaPath Townhall
          </Badge>
          <h1 className="font-heading text-2xl">{detail.title}</h1>
          {detail.description && (
            <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">{detail.description}</p>
          )}
        </div>

        <DeityImage src={detail.deity_image} title={detail.deity} chantId={chantId} deity={detail.deity} />

        <InspirationDashboard
          inspiration={inspiration}
          personalCount={personalCount}
          compact
        />

        {error && <p className="text-destructive text-sm mb-4">{error}</p>}

        <div className="flex flex-wrap gap-3 justify-center mb-6">
          {!syncing ? (
            <Button onClick={beginSyncedChanting} data-testid="begin-synced-chanting">
              <Play className="w-4 h-4 mr-1" /> Begin Synced Chanting
            </Button>
          ) : (
            <Badge className="px-3 py-1">
              <Volume2 className="w-3 h-3 mr-1 inline" />{" "}
              {textOnly ? "Text chanting active" : "Synced chanting active"}
            </Badge>
          )}
        </div>

        {syncing && !textOnly && (
          <audio ref={audioRef} src={audioSrc} preload="auto" className="hidden" data-testid="townhall-audio" />
        )}

        <ScrollingSlokaPanel
          slides={detail.slides}
          currentSeconds={
            syncing
              ? audioSeconds
              : detail.sync?.cycle?.offset_seconds ?? 0
          }
          language={lang}
          className="mb-6"
        />

        {!detail.slides?.length && (
          <p className="text-center text-sm text-muted-foreground mb-6">
            No sloka text yet — admins can add Sanskrit + transliteration via Edit.
          </p>
        )}

        <Card className="mb-6 bg-white/80 border-amber-100">
          <CardContent className="p-4 text-xs text-muted-foreground space-y-1">
            <p>
              <strong>Global cycles today (this chant):</strong>{" "}
              {(detail.global_stats?.total_cycles_completed_today ?? 0).toLocaleString()}
            </p>
            <p>
              <strong>Total chants by community:</strong>{" "}
              {(inspiration?.total_global_chants ?? inspiration?.total_cycles_today ?? 0).toLocaleString()}
            </p>
            <p className="italic pt-2 border-t">Storage: Cloudflare D1 (SQL) — Townhall is KV-free.</p>
          </CardContent>
        </Card>

        <AddMyCountForm chantId={chantId} token={token} onUpdated={handleOfflineUpdated} />
      </div>
    </div>
  );
}
